import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SellersService } from 'app/providers/api/services';
import { useApiMutation } from 'shared/hooks/useApiMutation';
import { useApiQuery } from 'shared/hooks/useApiQuery';
import { SellerBrandSettings } from 'shared/types/Sellers';

import useSellerPage from './useSellerPage';

const useSellerMarketingInfo = () => {
  const { t } = useTranslation();
  const {
    sellerInfo,
    isLoading: isLoadingSellerInfo,
    refetchSellerInfo,
  } = useSellerPage();
  const { sellerId } = useParams();

  const [brandLogo, setBrandLogo] = useState<string | null>(null);
  const [brandColor, setBrandColor] = useState<string>('#FFF');
  const { isPermittedToModifySeller } = useSellerPage();
  const [marketingStates, setMarketingStates] = useState<string[] | null>(null);
  const [marketingAllTags, setMarketingAllTags] = useState<string[] | null>(
    null,
  );
  const [marketingSellerTags, setMarketingSellerTags] = useState<
    string[] | null
  >(null);

  useEffect(() => {
    if (sellerInfo) {
      setBrandColor('#' + (sellerInfo?.brandColor || 'FFF'));
      setBrandLogo(sellerInfo?.logo || null);
    }
  }, [sellerInfo]);

  // Data Loading //
  const { isLoading: isLoadingMarketingTags } = useApiQuery(
    ['getSellerMarketingTags', sellerId],
    () => SellersService.fetchSellerMarketingTags(sellerId),
    {
      onSuccess: ({ data }) => {
        const sellerTags = data?.map((tag) => tag.title);
        setMarketingSellerTags(sellerTags);
      },
      cacheTime: 0,
    },
  );

  const { isLoading: isLoadingMarketingStates } = useApiQuery(
    ['getSellersMarketingStates', sellerId],
    () => SellersService.fetchSellerMarketingStates(sellerId),
    {
      onSuccess: ({ data }) => {
        setMarketingStates(data);
      },
    },
  );

  useApiQuery(
    ['getSellersMarketingAllTags'],
    () => SellersService.fetchSellersMarketingAllTags(),
    {
      onSuccess: ({ data }) => {
        const allTags = data.map((tag) => tag.title);
        setMarketingAllTags(allTags);
      },
    },
  );

  // Marketing States Updating //
  const { mutate: handleUpdateMarketingStates } = useApiMutation(
    ['updateSellerMarketingStates', sellerId],
    SellersService.updateSellerMarketingStates,
    {
      onSuccess: ({ data }) => {
        setMarketingStates(data);
      },
    },
  );

  // Marketing Tags Updating //
  const { mutate: handleUpdateMarketingTags } = useApiMutation(
    ['updateSellerMarketingtags', sellerId],
    SellersService.updateSellerMarketingTags,
    {
      onSuccess: ({ data }) => {
        const sellerTags = data.map((tag) => tag.title);
        setMarketingSellerTags(sellerTags);
      },
    },
  );

  // Brand Updating //
  const { mutateAsync: handleUpdateBrand, isLoading: isUpdatingBrand } =
    useApiMutation(
      ['updateSeller', sellerId],
      (values: SellerBrandSettings) =>
        SellersService.updateSeller({
          idOfSeller: sellerId,
          data: values,
        }),
      {
        onSuccess: (data) => {
          toast.success(t('Changes saved successfully'));
          setBrandColor('#' + data.brandColor);
          setBrandLogo(data.logo);
          refetchSellerInfo();
        },
      },
    );

  // Logo Uploading //
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const { mutateAsync: fetchLogoUploadUrl } = useApiMutation(
    ['getSellerLogoUploadUrl', sellerId],
    (extension: string) =>
      SellersService.fetchSellerLogoUploadUrl(sellerId, extension),
    {
      onError: () => {
        setIsUploadingLogo(false);
      },
      cacheTime: 0,
    },
  );

  const uploadSellerLogo = useCallback(
    async (file: File) => {
      if (file) {
        setIsUploadingLogo(true);

        let extension = file.name.split('.').pop()?.toLowerCase();
        if (extension === 'jpg') {
          extension = 'jpeg';
        }

        try {
          const { url } = await fetchLogoUploadUrl(extension);
          const urlFromS3 = url.split('?')[0].replace('s3.amazonaws.com/', '');

          await axios.put(url, file, {
            headers: {
              'Content-Type': `image/${extension}`,
            },
          });

          await handleUpdateBrand({
            brandColor: brandColor?.replace('#', ''),
            logo: urlFromS3,
          });
        } catch (error) {
          console.log(error);
        } finally {
          setIsUploadingLogo(false);
          refetchSellerInfo();
        }
      }
    },
    [brandColor, handleUpdateBrand, fetchLogoUploadUrl, refetchSellerInfo],
  );

  const deleteSellerLogo = useCallback(async () => {
    await handleUpdateBrand({
      brandColor: brandColor?.replace('#', ''),
      logo: '',
    });
  }, [brandColor, handleUpdateBrand]);

  return {
    sellerId,
    brandColor,
    brandLogo,
    isLoading:
      isLoadingSellerInfo || isLoadingMarketingTags || isLoadingMarketingStates,
    isPermittedToModifySeller,
    isUpdatingBrand: isUpdatingBrand || isUploadingLogo,
    marketingStates,
    marketingAllTags,
    marketingSellerTags,
    sellerInfo,
    handleUpdateMarketingStates,
    handleUpdateMarketingTags,
    handleUpdateBrand,
    uploadSellerLogo,
    deleteSellerLogo,
  };
};

export default useSellerMarketingInfo;
